import React from 'react'
import * as Yup from 'yup'
import { Button } from '@mui/material'
import { useFormik } from 'formik'
import { isValid } from 'date-fns'
import ErrorBoundary from '../../../../misc/ErrorBoundary'
import QuestionnaireField from './QuestionnaireField'
import { isHidden } from './questionnaireConditions'


const getFieldInitialValue = (field, initialValues) => {
    if (field.name in initialValues) {
        return initialValues[field.name]
    }

    if (field.guess) {
        return field.guess
    } else if (field.type === "checkbox") {
        return "false"
    } else if (field.type === "fixed-value") {
        return field.value
    } else if (field.type === "table") {
        return []
    } else {
        return ""
    }
}


const getValidationSchema = (fields) => {

    var yupObject = {}

    fields.filter(field => !!field.name && !isHidden(field)).forEach(field => {
        
        // Set validation
        if (field.required !== false && field.type === "table") {
            yupObject[field.name] = Yup.array().required()
            if (typeof field.table?.min_rows === "number") {
                yupObject[field.name] = Yup.array().min(field.table?.min_rows, `You must have at least ${field.table?.min_rows} entries`).required()
            }
            if (typeof field.table?.max_rows === "number") {
                yupObject[field.name] = Yup.array().max(field.table?.min_rows, `You are limited to ${field.table?.min_rows} entries`).required()
            }
        }  else if (field.required !== false && field.type === "date") {

            yupObject[field.name] = Yup.string().test({
                name: field.name,
                // skipAbsent: true,
                test(value, ctx) {

                    if (value === null || value === "") {
                        return ctx.createError({ message: 'Enter a valid date' })
                    }

                    var date = new Date(value)

                    if (!isValid(date)) {
                        return ctx.createError({ message: 'Enter a valid date' })
                    }

                    // if (field.min) {
                    //     var minDate = evaluateDateString({...context.answers, ...this.parent}, field.min)
                    //     if (minDate && compareAsc(date, minDate) < 0) {
                    //         return ctx.createError({ message: `Enter a date after ${format(minDate, "do LLLL u")}` })
                    //     }
                    // }

                    // if (field.max) {
                    //     var maxDate = evaluateDateString({...context.answers, ...this.parent}, field.max)
                    //     if (maxDate && compareAsc(date, maxDate) > 0) {
                    //         return ctx.createError({ message: `Enter a date before ${format(maxDate, "do LLLL u")}` })
                    //     }
                    // }

                    return true
                }
            })
        } else if (field.type === "phone-number" && field.required !== false) {
            
            var fieldValidation = Yup.string().matches(/^\+[0-9][0-9\- ]{7,20}[0-9]$/g, "Enter a valid phone number")
            yupObject[field.name] = fieldValidation
        
        } else if (field.name && field.required !== false) {

            var regex = field.regex ? field.regex : "^[“”‘’ -~À-Ö\p{Sc}\p{So}\p{Mn}\p{P}\p{Z}À-ÿ\w\n]*$"
            var regexMessage = field.regexMessage ? field.regexMessage : "Only use standard European characters"
            var fieldValidation = Yup.string().matches(regex, regexMessage)

            if (field.min) {
                fieldValidation = fieldValidation.test('min', `You must enter at least ${field.min} characters`, val => val?.length >= field.min)
            }
            if (field.max) {
                fieldValidation = fieldValidation.test('max', `You are limited to ${field.max} characters`, val => val?.length <= field.max)
            }
            if (field.required !== false) {
                fieldValidation = fieldValidation.required("This field is required")
            }

            yupObject[field.name] = fieldValidation
        }
    })

    return Yup.object(yupObject)
}


export default function FormFields ({
    fields,
    initialValues = {},
    onChange = async (formik) => {},
    onSubmit = async (values) => {},
    onCancel = null,
    submitButtonText = "Submit"
}) {

    // Set intial value to guess
    fields.filter(field => !!field.name && !isHidden(field)).forEach(field => {
        initialValues[field.name] = getFieldInitialValue(field, initialValues)
    })

    var formik = useFormik({

        initialValues: initialValues,
        validationSchema: getValidationSchema(fields),
        // validateOnMount: true,
        enableReinitialize: true,

        onSubmit: async (values) => {
            return await onSubmit(values)
        }
    })

    const applyConditionsToFormik = () => {

        var displayedFieldKeys = []

        fields.forEach(field => {
            if (!isHidden(field, formik.values)) {
                displayedFieldKeys.push(field.name)
            }
        })

        fields.forEach(field => {
            if (!displayedFieldKeys.includes(field.name)) {
                delete formik.values[field.name]
                delete formik.errors[field.name]
            }
            // This is for when fields which are initially hidden and then become shown, 
            // the correct initial value needs to be reassigned.
            if (displayedFieldKeys.includes(field.name) && !Object.keys(formik.values).includes(field.name)) {
                formik.values[field.name] = getFieldInitialValue(field, initialValues)
            }
        })
    }

    React.useEffect(() => {
        applyConditionsToFormik()
    })

    React.useEffect(() => {
        onChange(formik)
    }, [formik.values, formik.errors])

    console.log(formik.values, formik.errors)

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <div className="form-fields" style={{maxHeight: "50vh", overflowY: "auto"}}>
                {fields.map((field, i) => {

                    if (isHidden(field, formik.values) || field.type === "fixed-value") {
                        return <React.Fragment key={`${i}-${field.name}`}></React.Fragment>
                    }

                    return (
                        <ErrorBoundary key={`${i}-${field.name}`}>
                            <div>
                                <QuestionnaireField
                                    field={field}
                                    formik={formik}
                                    autoFocus={i === 0} />

                                <span className="warning">
                                    {formik.submitCount && formik.errors[field.name] ? formik.errors[field.name] : null}
                                </span>
                            </div>
                        </ErrorBoundary>
                    )
                })}
            </div>


            
            <div className="form-buttons" style={{marginTop: "20px"}}>
                {onCancel ? (
                    <Button 
                        className="btn btn-sm" 
                        onClick={() => onCancel()}
                        disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                ) : null}

                <Button
                    className="btn btn-sm" 
                    color="success" 
                    type="submit" 
                    variant="contained" 
                    disabled={formik.isSubmitting || !!Object.keys(formik.errors).length}>
                    {submitButtonText}
                </Button>
            </div>
        </form>
    )
}